import { FC, Fragment } from "react";
import { useIntl } from "react-intl";

import "./ScheduleStopList.scss";
import { DateTimeFormat, formatDate } from "helpers/dateTimeHelper";
import ScheduleStopListItem from "../ScheduleStopListItem/ScheduleStopListItem";
import ScheduleTransit from "../ScheduleTransit/ScheduleTransit";
import { FreightModeBaseTypes } from "../../../../../../../constants";
import {
  getFlightScheduleDateTime,
  getOceanVesselCarrierLabel,
  getTransitMessage,
  getTransportLocationName,
  type OceanVesselLeg,
} from "../../scheduleUtils";

type ScheduleStopListProps = {
  transitStops: any;
  freightModeType: typeof FreightModeBaseTypes[keyof typeof FreightModeBaseTypes];
  oceanVesselLegs: OceanVesselLeg[];
};

const ScheduleStopList: FC<ScheduleStopListProps> = function (props) {
  const { transitStops, freightModeType, oceanVesselLegs } = props;
  const intl = useIntl();
  const isAirFreight = freightModeType === "air";
  const isOceanFreight = freightModeType === "ocean";
  const departureKey = isAirFreight ? "flight_departure" : "departure_seaport";
  const arrivalKey = isAirFreight ? "flight_arrival" : "arrival_seaport";

  if (isOceanFreight && oceanVesselLegs.length > 0) {
    return (
      <div className="schedule-stop-list schedule-stop-list--ocean">
        {oceanVesselLegs.map((leg, legIndex) => {
          const departureStop = leg.departure;
          const arrivalStop = leg.arrival;
          const carrierLabel = departureStop ? getOceanVesselCarrierLabel(departureStop) : undefined;

          return (
            <div className="schedule-stop-list__vessel-block" key={leg.stop_id}>
              <div className="text-sm font-medium schedule-stop-list__vessel-heading">
                {intl.formatMessage(
                  {
                    id: "tracking.vesselScheduleLegTitle",
                    defaultMessage: "Vessel {number}",
                  },
                  { number: legIndex + 1 },
                )}
              </div>
              {departureStop && (
                <ScheduleStopListItem
                  transportLocationName={getTransportLocationName(false, departureStop, intl)}
                  dateTime={(() => {
                    const dt = getFlightScheduleDateTime(false, departureStop);
                    return dt.isValid()
                      ? formatDate(dt, DateTimeFormat.dateFormat)
                      : intl.formatMessage({
                          id: "tracking.dateNotProvided",
                          defaultMessage: "Date not provided",
                        });
                  })()}
                  isAirFreight={false}
                  carrierName={carrierLabel}
                  showLine={Boolean(arrivalStop)}
                />
              )}
              {arrivalStop && (
                <ScheduleStopListItem
                  transportLocationName={getTransportLocationName(false, arrivalStop, intl)}
                  dateTime={(() => {
                    const dt = getFlightScheduleDateTime(false, arrivalStop);
                    return dt.isValid()
                      ? formatDate(dt, DateTimeFormat.dateFormat)
                      : intl.formatMessage({
                          id: "tracking.dateNotProvided",
                          defaultMessage: "Date not provided",
                        });
                  })()}
                  isAirFreight={false}
                  showLine={false}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="schedule-stop-list">
      {transitStops.map((stop: any, index: number) => {
        const stopDateTime = getFlightScheduleDateTime(isAirFreight, stop);
        return (
          <Fragment key={`${stop.type}-${stop.stop_id ?? index}`}>
            <ScheduleStopListItem
              transportLocationName={getTransportLocationName(isAirFreight, stop, intl)}
              dateTime={
                stopDateTime.isValid()
                  ? formatDate(stopDateTime, isAirFreight ? "DD MMM YYYY, HH:mm" : DateTimeFormat.dateFormat)
                  : intl.formatMessage({
                      id: "tracking.dateNotProvided",
                      defaultMessage: "Date not provided",
                    })
              }
              isAirFreight={isAirFreight}
              {...(stop.type === departureKey &&
                transitStops?.[index + 1]?.type === arrivalKey && {
                  carrierName: isAirFreight ? stop.label_v2 || stop.label : stop.vessel_number,
                })}
              showLine={
                index !== transitStops.length - 1 &&
                stop.type === departureKey &&
                transitStops?.[index + 1]?.type === arrivalKey
              }
            />
            {isAirFreight && stop.type === arrivalKey && transitStops?.[index + 1]?.type === departureKey && (
              <ScheduleTransit transitMessage={getTransitMessage(stop, transitStops[index + 1], intl)} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

export default ScheduleStopList;
